import React, { useMemo, useState } from 'react';
import { Box, Button, Grid, SearchBar, ErrorInfo, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import FormControl from 'web-library/stories/atom/FormControl/FormControl';
import * as ResitCoordinatorQuery from '../api/candidate/resit/@coordinator/queries-services/queries';
import { CandidateResitRegistrationSchema } from '../models/@candidate/candidateResitRegistrationSchema';
import { selectCandidateResitRegistration } from '../store/resitSlice';

const useStyles = makeStyles()((theme) =>
({
  container: {
    display: 'flex',
    flexDirection: 'column',

    marginBottom: theme.spacing(2),
  },
  formControlRoot: {
    width: '100%',
    marginBottom: theme.spacing(3),
  },
  popTitle: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(2),
  },
  popSubtitle: {
    marginBottom: theme.spacing(3),
  },
  keyLabel: {
    color: theme.color.text.textHeader,
  },
  keyLabelNull: {
    color: theme.color.text.textTertiary,
  },
  keyValue: {
    color: theme.color.text.textPrimary,
    marginBottom: theme.spacing(2),
  },
  keyValueNull: {
    color: theme.color.text.textTertiary,
    marginBottom: theme.spacing(2),
  },
}));

export interface AddCandidateProps {
  /** current candidates listing */
  existingCandidates: readonly string[];
  /** to handle on add candidate
   * */
  onAddCandidate: (newCandidateData: CandidateResitRegistrationSchema) => void;
  handleClose: () => void;
}

const AddCandidate: React.FC<AddCandidateProps> = (props: AddCandidateProps) => {
  const { existingCandidates, handleClose, onAddCandidate } = props;
  /** local states
   * */
  const [searchParam, setSearchParam] = useState('');

  /** component hooks
   * */
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [emptySearch, setEmptySearch] = useState(false);

  const resitCandidateData = useSelector(selectCandidateResitRegistration);

  const { mutate, data, error } = ResitCoordinatorQuery.useResitCandidateSearch(
    (response) => {
      // do nothing
    },
    (err) => {
      // do nothing
    }
  );

  const onSearchParamChange = (searchText: string) => {
    setSearchParam(searchText);
  };

  const onCandidateSearch = (str: string) => {
    if (str !== '') {
      mutate({
        nric: str,
        // examCode: 'GCEO',
        examCode: resitCandidateData.examLevel?.code || '',
      });
    } else {
      setEmptySearch(true);
    }
  };

  const alreadyExist = useMemo(() => existingCandidates.includes(searchParam), [
    existingCandidates,
    searchParam,
  ]);

  return (
    <Box className={classes.container}>
      <Typography variant="h4" className={classes.popTitle}>
        {t('resit->>RegisterResit.Add a Candidate')}
      </Typography>

      <Typography variant="body2" className={classes.popSubtitle}>
        {t(
          'resit->>RegisterResit.Please add a candidate by filling up their NRIC/FIN/Passport Number.'
        )}
      </Typography>

      <Grid xs={12}>
        <FormControl className={classes.formControlRoot}>
          <Grid xs={12}>
            <SearchBar
              name="addCandidate"
              ariaLabel="searchInput"
              defaultValue={searchParam}
              onSearch={(str) => {
                setEmptySearch(str === '');
                onSearchParamChange(str.toUpperCase());
                onCandidateSearch(str.toUpperCase());
              }}
              placeholder={`Search for ${t(
                'resit->>RegisterResit.Candidate NRIC/FIN/Passport No.'
              )}`}
            />
          </Grid>
          {emptySearch && <ErrorInfo label="Please enter NRIC/FIN/Passport No." />}

          {alreadyExist && <ErrorInfo label="This candidate is already in the list." />}

          {error && !emptySearch && <ErrorInfo label={error?.response?.data.message || ''} />}
        </FormControl>
      </Grid>

      <>
        <Typography
          variant="h5"
          className={data?.data?.statutoryName ? classes.keyLabel : classes.keyValueNull}
        >
          Eligible candidate
        </Typography>
        <Typography
          variant="body1"
          className={data?.data?.statutoryName ? classes.keyValue : classes.keyValueNull}
        >
          {data?.data?.statutoryName || '-'}
        </Typography>

        <Grid xs={12}>
          <Button
            id="button-addCandidate-addCandidate"
            disabled={!data}
            size="small"
            type="submit"
            variant="contained"
            onClick={() => {
              if (data) {
                onAddCandidate(data.data);
                setSearchParam('');
                handleClose();
              }
            }}
          >
            Add Candidate
          </Button>
        </Grid>
      </>
    </Box>
  );
};
export default AddCandidate;
